<template>
  <div>
    <p v-if="!abbreviatedSchedule" class="text-h4 mb-8">Schedule</p>
    <div class="flex items-center flex-wrap" :class="abbreviatedSchedule ? 'justify-start' : 'justify-between'">
      <Pill :color="topLineProperties.color" class="min-w-max">{{ topLineProperties.name }}</Pill>
      <WeeklySchedule
        v-if="topLineProperties.showDays"
        :selectedDays="effectiveSchedule.days"
        :closed="scheduleDisplayClosed"
        :blueVariant="topLineProperties.color === 'blue'"
        class="xs-screen-fix ml-16"
      />
      <div
        v-if="!topLineProperties.showDays && abbreviatedSchedule"
        class="text-body ml-16"
        :class="{ 'text-grey-text-subdued': scheduleDisplayClosed }"
      >
        {{ abbreviatedDateText }}
      </div>
    </div>
    <div
      v-if="!abbreviatedSchedule"
      :class="sanitizedScheduleType === 'occasional' ? 'mt-16' : 'mt-[25px]'"
      data-test-id="full-schedule"
    >
      <div v-if="sanitizedScheduleType === 'occasional'" class="text-body-3 mb-16">
        For babysitters available on an as-needed basis
      </div>
      <div v-if="isNannySchedule" class="mb-16 mt-8">
        <div class="text-overline mt-[24px]">Times</div>
        <div class="text-strong-body" data-test-id="nanny-schedule">
          <div v-for="time in effectiveSchedule.hours" :key="time">{{ convertNannyTimeCasing(time) }}</div>
        </div>
      </div>
      <div class="text-overline">Starts</div>
      <div class="text-strong-body" data-test-id="date-text">
        {{ dateText }}
        <span v-if="effectiveSchedule.isStartDateFlexible" class="text-grey-text-subdued text-caption ml-8">Flexible</span>
      </div>
      <div v-if="showHoursSection" data-test-id="hours-section">
        <div class="text-overline mt-[24px]">Hours</div>
        <div class="text-strong-body">
          {{ timeProperties.text }}
          <span v-if="timeProperties.nextDay" class="text-grey-text-subdued text-caption ml-8">Next day</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { isBefore, isAfter, format } from 'date-fns';
import lowerCase from 'lodash/lowerCase';
import capitalize from 'lodash/capitalize';

import WeeklySchedule from './WeeklySchedule.vue';
import Pill from '../pill/Pill.vue';
import { buildSchedulePayloadForJobSchedule } from '@/models/jobScheduleDisplay';

interface TopLineProperties {
  color: string;
  name?: string;
  showDays?: boolean;
}

interface TimeProperties {
  text: string;
  nextDay?: boolean;
}

export default {
  name: 'JobSchedule',
  components: {
    WeeklySchedule,
    Pill,
  },
  props: {
    abbreviatedSchedule: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * Schedule context: `careType`, `scheduleType`, `schedule` (nanny `hours` → `days` when careType is NANNY).
     * `isActive` (boolean): when `false`, pill/week chips use inactive (grey) styling. Always pass an object (e.g. `{ scheduleType: '', schedule: {}, isActive: false }` if empty).
     */
    job: {
      type: Object,
      required: true,
    },
    /**
     * When `true` or `false`, overrides `job.isActive` for inactive (grey) schedule styling. Omit to use `job.isActive`.
     * `default: undefined` avoids Vue 2 coercing a missing Boolean prop to `false`.
     */
    displayAsActive: {
      type: Boolean,
      required: false,
      default: undefined,
    },
  },
  computed: {
    scheduleDisplayClosed(): boolean {
      if (this.displayAsActive !== undefined) {
        return !this.displayAsActive;
      }
      return !this.job.isActive;
    },
    jobSchedulePayload(): { scheduleType: string; schedule: Record<string, unknown> } {
      const j = this.job;
      return buildSchedulePayloadForJobSchedule({
        careType: j.careType,
        scheduleType: j.scheduleType,
        schedule: j.schedule,
      });
    },
    effectiveScheduleType(): string {
      return this.jobSchedulePayload.scheduleType;
    },
    effectiveSchedule(): Record<string, unknown> {
      return this.jobSchedulePayload.schedule;
    },
    topLineProperties(): TopLineProperties {
      let returnObj: TopLineProperties = { color: 'brand' };
      if (this.sanitizedScheduleType === 'one_time') returnObj = { name: 'One-time', color: 'yellow' };
      if (this.sanitizedScheduleType === 'regularly_scheduled') returnObj = { name: 'Recurring', color: 'green', showDays: true };
      if (this.sanitizedScheduleType === 'occasional') returnObj = { name: 'Occasional', color: 'blue' };
      if (this.sanitizedScheduleType === 'part_time') returnObj = { name: 'Part Time', color: 'blue', showDays: true };
      if (this.sanitizedScheduleType === 'full_time') returnObj = { name: 'Full Time', color: 'green', showDays: true };
      if (this.scheduleDisplayClosed) returnObj.color = 'grey';
      return returnObj;
    },
    sanitizedScheduleType(): String {
      return this.effectiveScheduleType?.toLowerCase() ?? '';
    },
    dateText(): String {
      const today = new Date();
      const sch = this.effectiveSchedule;
      const startTime = sch.startTime ? sch.startTime : '00:00';
      const startDate = new Date(`${sch.startDate}T${startTime}`);
      if (!sch.startDate || (isBefore(startDate, today) && !sch.isStartDateFlexible) || sch.isUrgent) return 'ASAP';
      return format(startDate, 'E, LLL d');
    },
    timeProperties(): TimeProperties {
      const sch = this.effectiveSchedule;
      const startTime = new Date(`${sch.startDate} ${sch.startTime}`);
      const endTime = new Date(`${sch.startDate} ${sch.endTime}`);

      const returnObj: TimeProperties = {
        text: `${format(startTime, 'h:mm a')} – ${format(endTime, 'h:mm a')}`,
      };

      if (!isAfter(endTime, startTime)) returnObj.nextDay = true;

      return returnObj;
    },
    abbreviatedDateText(): String {
      const today = new Date();
      const sch = this.effectiveSchedule;
      const startTime = sch.startTime ? sch.startTime : '00:00';
      const startDate = new Date(`${sch.startDate}T${startTime}`);
      if (sch.isStartDateFlexible) return 'Flexible';
      if (!sch.startDate || isBefore(startDate, today) || sch.isUrgent) return 'ASAP';
      return format(startDate, 'E, LLL d');
    },
    isNannySchedule(): Boolean {
      return this.sanitizedScheduleType === 'part_time' || this.sanitizedScheduleType === 'full_time';
    },
    showHoursSection(): Boolean {
      return !this.isNannySchedule && !!this.effectiveSchedule.startTime;
    },
  },
  methods: {
    convertNannyTimeCasing(str): String {
      return capitalize(lowerCase(str));
    },
  },
};
</script>

<style lang="scss" scoped>
.fill-available {
  width: -moz-available; /* WebKit-based browsers will ignore this. */
  width: -webkit-fill-available; /* Mozilla-based browsers will ignore this. */
  width: stretch;
}

.xs-screen-fix {
  margin-top: 0.5rem;
  @media only screen and (min-width: 370px) {
    margin-top: 0px;
  }
}
</style>
